import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChevronLeft,
  faChevronRight,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Table, Badge, InputGroup, Form, Button, Row, Col } from "@themesberg/react-bootstrap";
import { userTdsReport } from "../../../pages/api";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const getStatusBadgeStyle = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
    case "inactive":
      return { background: "#f59e0b", color: "#ffffff" };
    case "success":
    case "completed":
    case "active":
    case "issued":
    case "calculated":
      return { background: "#10b981", color: "#ffffff" };
    case "failed":
    case "blocked":
    case "cancelled":
      return { background: "#ef4444", color: "#ffffff" };
    case "refunded":
      return { background: "#6366f1", color: "#ffffff" };
    default:
      return { background: "#6b7280", color: "#ffffff" };
  }
};

const capitalize = (value) =>
    String(value || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

export default function UserTds() {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({
    gross_commission: 0,
    tds_amount: 0,
    net_payable: 0,
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const searchDebounceRef = useRef(null);

  const fetchTds = async (page = 1, perPage = 10, searchTerm = "", from = "", to = "") => {
    try {
      setLoading(true);
      const response = await userTdsReport({
        page,
        per_page: perPage,
        search: searchTerm || undefined,
        date_from: from || undefined,
        date_to: to || undefined,
      });

      const apiData = response?.data?.data || {};
      setRecords(Array.isArray(apiData.records) ? apiData.records : []);
      setSummary({
        gross_commission: Number(apiData?.summary?.gross_commission ?? 0),
        tds_amount: Number(apiData?.summary?.tds_amount ?? 0),
        net_payable: Number(apiData?.summary?.net_payable ?? 0),
      });
      setPagination({
        current_page: Number(apiData?.pagination?.current_page ?? page) || page,
        last_page: Number(apiData?.pagination?.last_page ?? 1) || 1,
        per_page: Number(apiData?.pagination?.per_page ?? perPage) || perPage,
        total: Number(apiData?.pagination?.total ?? 0) || 0,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to fetch TDS report.");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTds(1, pagination.per_page, search, dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchTds(1, pagination.per_page, search, dateFrom, dateTo);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dateFrom, dateTo]);


  const getShowingRange = () => {
    const total = pagination.total;
    const current = pagination.current_page;
    const perPage = pagination.per_page;

    if (!total) return { start: 0, end: 0 };

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page;
    const current = pagination.current_page;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  const resetFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div className="lsg-page">
      <div className="d-flex justify-content-between flex-wrap gap-3 py-2 mb-3">
        <div>
          <h2 className="lsg-page-title">TDS</h2>
          <p className="lsg-page-subtitle">
            Reportable TDS @1% on commission. Wallet credits remain gross (unchanged).
          </p>
        </div>
      </div>

      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted small">Gross Commission</div>
              <div className="fs-5 fw-bold">{formatCurrency(summary.gross_commission)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted small">TDS Deducted (reportable)</div>
              <div className="fs-5 fw-bold">{formatCurrency(summary.tds_amount)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="text-muted small">Net Payable</div>
              <div className="fs-5 fw-bold">{formatCurrency(summary.net_payable)}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
            <InputGroup style={{ maxWidth: "260px" }}>
              <InputGroup.Text className="bg-white">
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
            <Form.Control
              type="date"
              style={{ maxWidth: "160px" }}
              value={dateFrom}
              onClick={(e) => e.currentTarget.showPicker?.()}
              onChange={(e) => setDateFrom(e.target.value)}
              title="From"
            />
            <Form.Control
              type="date"
              style={{ maxWidth: "160px" }}
              value={dateTo}
              onClick={(e) => e.currentTarget.showPicker?.()} a
              onChange={(e) => setDateTo(e.target.value)}
              title="To"
            />
            <Button variant="outline-secondary" onClick={resetFilters}>
              Reset
            </Button>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User ID</th>
                  <th>Commission Ref.</th>
                  <th>Level</th>
                  <th className="text-end">Gross</th>
                  <th className="text-end">TDS %</th>
                  <th className="text-end">TDS</th>
                  <th className="text-end">Net Payable</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      Loading…
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      <FontAwesomeIcon icon={faReceipt} className="me-2" />
                      No TDS records found.
                    </td>
                  </tr>
                ) : (
                  records.map((item) => (
                    <tr key={item.commission_log_id}>
                      <td>{formatDate(item.date)}</td>
                      <td>{item.user_id || "—"}</td>
                      <td>#{item.commission_log_id}</td>
                      <td>{item.level ?? "—"}</td>
                      <td className="text-end">{formatCurrency(item.gross_amount)}</td>
                      <td className="text-end">{Number(item.tds_percentage || 1)}%</td>
                      <td className="text-end">{formatCurrency(item.tds_amount)}</td>
                      <td className="text-end fw-semibold">{formatCurrency(item.net_amount)}</td>
                      {/* <td>
                        <Badge bg="info" className="text-dark">
                          {item.status || "calculated"}
                        </Badge>
                         </td> */}
                      <td>
                        <Badge className="px-3 py-2 rounded-pill" style={getStatusBadgeStyle(item.status)}>{capitalize(item.status)}</Badge>
                      </td>

                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                className="course-page-btn"
                onClick={() =>
                  fetchTds(
                    pagination.current_page - 1,
                    pagination.per_page,
                    search.trim(),
                    dateFrom,
                    dateTo
                  )
                }
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {getPageItems().map((p, i) =>
                p === "..." ? (
                  <span key={i} className="px-2">
                    ...
                  </span>
                ) : (
                  <Button
                    key={p}
                    variant="outline-primary"
                    disabled={loading}
                    className={`course-page-btn ${p === pagination.current_page
                      ? "course-page-btn-active"
                      : ""
                      }`}
                    onClick={() =>
                      fetchTds(
                        p,
                        pagination.per_page,
                        search.trim(),
                        dateFrom,
                        dateTo
                      )
                    }
                  >
                    {p}
                  </Button>
                )
              )}

              <Button
                variant="outline-primary"
                disabled={
                  pagination.current_page >= pagination.last_page || loading
                }
                className="course-page-btn"
                onClick={() =>
                  fetchTds(
                    pagination.current_page + 1,
                    pagination.per_page,
                    search.trim(),
                    dateFrom,
                    dateTo
                  )
                }
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
